import { useCallback, useEffect, useState } from 'react';
import api from '@services/api';
import type { SportModuleMatrixData } from '@utils/sportModules';

let cachedMatrix: SportModuleMatrixData | null = null;
let cacheAt = 0;
const CACHE_MS = 60_000;

export function useSportModules() {
  const [data, setData] = useState<SportModuleMatrixData | null>(cachedMatrix);
  const [loading, setLoading] = useState(!cachedMatrix);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && cachedMatrix && now - cacheAt < CACHE_MS) {
      setData(cachedMatrix);
      setLoading(false);
      return cachedMatrix;
    }
    setLoading(true);
    try {
      const res = await api.get('/sport-modules/matrix');
      const next = res.data?.data as SportModuleMatrixData;
      cachedMatrix = next;
      cacheAt = Date.now();
      setData(next);
      setError(null);
      return next;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load sport modules';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

export function invalidateSportModulesCache() {
  cachedMatrix = null;
  cacheAt = 0;
}
